// src/App.tsx

/**
 * Frontend application entry point for the Quantik Mind enterprise UI.
 *
 * This module composes the main React application shell, registers route
 * definitions, mounts global providers, and applies the protected layout used
 * by authenticated pages.
 *
 * The frontend exposes two main navigation surfaces:
 *
 * - A public login route used for authentication.
 * - A protected application area rendered with sidebar and top navigation.
 */

import React from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  AppBar,
  Box,
  CssBaseline,
  Toolbar,
  Typography,
} from '@mui/material';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import logo from './assets/logo_no_bg.png';
import { logout } from './api/auth';
import { ErrorBoundary } from './components/ErrorBoundary';
import Sidebar from './components/Sidebar';
import ActionButton from './components/common/ActionButton';
import { AuthProvider } from './context/authContext';
import { ProjectProvider } from './context/ProjectContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import MetricsResultsPage from './pages/MetricsResults';
import BenchmarkOnboardingPage from './pages/BenchmarkOnboardingPage';
import OnboardingEntryPage from './pages/OnboardingEntryPage';
import OnboardingPage from './pages/OnboardingPage';
import PlanSelectionPage from './pages/PlanSelectionPage';
import ProtectedRoute from './pages/ProtectedRoute';
import QuantumSelectionPage from './pages/QuantumSelection';
import SettingsPage from './pages/Settings';
import SkippedTestsAnalysisPage from './pages/SkippedTestsAnalysis';
import SignupPage from './pages/SignupPage';
import VerifyEmailNoticePage from './pages/VerifyEmailNoticePage';
import VerifyEmailResultPage from './pages/VerifyEmailResultPage';
import AdminOverviewPage from './pages/AdminOverviewPage';

/**
 * Render the shared layout used by all protected application pages.
 *
 * This layout provides:
 * - the enterprise sidebar
 * - the fixed top application bar
 * - the logout action
 * - the main content container for authenticated views
 *
 * @param children - Page content rendered inside the protected layout.
 * @returns The protected page shell.
 */
function MainLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0 }}>
      <CssBaseline />

      <Sidebar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          flexBasis: 0,
          minWidth: 0,
          maxWidth: '100%',
          px: { xs: 1.5, sm: 2.5, md: 3 },
          py: { xs: 2, md: 3 },
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: '#0D1117',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Toolbar>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                minWidth: 0,
                gap: 1,
              }}
            >
              <img
                src={logo}
                alt="Quantik Mind logo"
                style={{ height: '32px', marginRight: '12px' }}
              />

              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: '#39ff14',
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  lineHeight: 1,
                  minWidth: 0,
                }}
              >
                Quantik Mind
              </Typography>

              {/* Flexible spacer used to push the logout action to the right. */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Clear the persisted session and redirect the user to the login page. */}
              <ActionButton
                startIcon={<LogoutIcon sx={{ color: '#39ff14' }} />}
                onClick={() => {
                  void logout();
                }}
                sx={{
                  px: { xs: 1.25, sm: 2 },
                  fontSize: '0.875rem',
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Logout
                </Box>
              </ActionButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Toolbar offset keeps page content below the fixed top bar. */}
        <Toolbar />

        {children}
      </Box>
    </Box>
  );
}

/**
 * Compose the full enterprise application.
 *
 * This component mounts:
 * - the router
 * - the top-level error boundary
 * - the authentication provider
 * - the project context provider
 * - all public and protected routes
 *
 * @returns The root React application.
 */
export default function App() {
  return (
    <Router>
      <ErrorBoundary>
        <AuthProvider>
          <SubscriptionProvider>
            <ProjectProvider>
              <CssBaseline />

              <Routes>
                {/* Public route rendered without the protected application shell. */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/verify-email/check" element={<VerifyEmailNoticePage />} />
                <Route path="/verify-email" element={<VerifyEmailResultPage />} />

                {/* Protected routes rendered inside the shared enterprise layout. */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <HomePage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/admin/overview"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <AdminOverviewPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/quantum"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <QuantumSelectionPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/results"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <MetricsResultsPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/runs/:runId/skipped-tests"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <SkippedTestsAnalysisPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute
                      element={
                        <MainLayout>
                          <SettingsPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/getting-started/plan"
                  element={
                    <ProtectedRoute
                      requireVerified
                      element={
                        <MainLayout>
                          <PlanSelectionPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute
                      requireVerified
                      element={
                        <MainLayout>
                          <OnboardingEntryPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/onboarding/project"
                  element={
                    <ProtectedRoute
                      requireVerified
                      element={
                        <MainLayout>
                          <OnboardingPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                <Route
                  path="/onboarding/benchmark"
                  element={
                    <ProtectedRoute
                      requireVerified
                      element={
                        <MainLayout>
                          <BenchmarkOnboardingPage />
                        </MainLayout>
                      }
                    />
                  }
                />

                {/* Reserved catch-all route for future 404 handling. */}
                {/* <Route path="*" element={<NotFoundPage />} /> */}
              </Routes>
            </ProjectProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
}
